// src/components/ui/LocationButton.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaSpinner } from 'react-icons/fa';

interface LocationButtonProps {
    onClick: () => void;
    isLoading: boolean;
    theme?: {
        primaryColor: string;
        textColor: string;
        backgroundColor: string;
    };
    loadingText?: string;
    buttonText?: string;
    className?: string;
}

const LocationButton: React.FC<LocationButtonProps> = ({
    onClick,
    isLoading,
    theme,
    loadingText = "Getting location...",
    buttonText = "Get Current Location",
    className = "",
}) => {
    return (
        <motion.button
            onClick={onClick}
            disabled={isLoading}
            className={`relative overflow-hidden rounded-xl py-4 px-6 text-white font-semibold text-sm transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed ${className}`}
            style={{
                background: isLoading
                    ? `linear-gradient(135deg, ${theme?.primaryColor || "#ea580c"}80, ${theme?.primaryColor || "#ea580c"}60)`
                    : `linear-gradient(135deg, ${theme?.primaryColor || "#ea580c"}, ${theme?.primaryColor || "#ea580c"}dd)`,
                boxShadow: `0 4px 20px ${theme?.primaryColor || "#ea580c"}40`,
            }}
            whileHover={!isLoading ? {
                boxShadow: `0 8px 25px ${theme?.primaryColor || "#ea580c"}50`,
                y: -2
            } : {}}
            whileTap={!isLoading ? { scale: 0.98 } : {}}
        >
            {/* Background Animation */}
            <motion.div
                className="absolute inset-0 opacity-20"
                style={{
                    background: `linear-gradient(45deg, transparent 30%, ${theme?.backgroundColor || "#ffffff"}40 50%, transparent 70%)`,
                }}
                animate={{
                    x: isLoading ? ["-100%", "200%"] : "-100%",
                }}
                transition={{
                    duration: 1.5,
                    repeat: isLoading ? Infinity : 0,
                    ease: "easeInOut",
                }}
            />

            {/* Content */}
            <div className="relative flex items-center justify-center gap-3">
                {isLoading ? (
                    <>
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                            <FaSpinner size={16} />
                        </motion.div>
                        <span>{loadingText}</span>
                    </>
                ) : (
                    <>
                        <motion.div
                            whileHover={{ scale: 1.1, rotate: [0, -10, 10, 0] }}
                            transition={{ duration: 0.3 }}
                        >
                            <FaMapMarkerAlt size={16} />
                        </motion.div>
                        <span>{buttonText}</span>
                    </>
                )}
            </div>

            {/* Pulse Effect */}
            {!isLoading && (
                <motion.div
                    className="absolute inset-0 rounded-xl border-2 border-white opacity-0"
                    animate={{
                        scale: [1, 1.05, 1],
                        opacity: [0, 0.3, 0],
                    }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
            )}
        </motion.button>
    );
};

export default LocationButton;